import React, { useState, useEffect, useMemo, memo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchProducts, fetchSlides, fetchContent, fetchReviews, getImageUrl } from '../api/api';
import { useSeoSettings } from '../hooks/useSeoSettings';
import { useCurrency } from '../context/CurrencyContext';
import SEO, { getOrganizationSchema } from '../components/SEO';
import './Home.css';
import './ProductCard.css';

const StarIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M20 6L9 17l-5-5"/>
  </svg>
);

function ReviewsStrip({ reviews, totalCount, loaded }) {
  const [offset, setOffset] = useState(0);
  const VISIBLE = 4;
  const maxOffset = Math.max(0, reviews.length - VISIBLE);
  const prev = () => setOffset(o => Math.max(0, o - 1));
  const next = () => setOffset(o => Math.min(maxOffset, o + 1));
  const visible = reviews.slice(offset, offset + VISIBLE);
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const displayReviews = isMobile ? reviews : visible;
  // Reserve the strip's height while reviews are loading so they don't pop in and shift the page (CLS)
  if (!loaded) return <div className="reviews-strip reviews-strip-loading" aria-hidden="true" />;
  if (!reviews.length) return null;
  return (
    <div className="reviews-strip">
      <div className="reviews-strip-inner">
        <div className="reviews-summary">
          <div className="reviews-summary-label">Excellent</div>
          <div className="reviews-summary-stars">
            {[1,2,3,4,5].map(i => <div key={i} className="rs"><StarIcon /></div>)}
          </div>
          <div className="reviews-count">
            <a href="https://www.google.com/maps/place/Green+Wheels+Scooters/review" target="_blank" rel="noopener noreferrer">
              Based on {totalCount || reviews.length} reviews
            </a>
          </div>
          <div className="reviews-google">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Google Reviews
          </div>
        </div>
        <div className="reviews-cards-wrap">
          <div className="reviews-cards">
            {displayReviews.map((r, i) => (
              <div key={i} className="review-card">
                <div className="review-card-stars">
                  {[1,2,3,4,5].map(s => (
                    <div key={s} className={`rs${s > (r.stars || 5) ? ' rs-empty' : ''}`}><StarIcon /></div>
                  ))}
                  <div className="review-verified"><CheckIcon />Verified</div>
                </div>
                <p className="review-title">{r.title}</p>
                <p className="review-body">{r.body}</p>
                <div className="review-author-row">
                  {r.photo && <img src={r.photo} alt={r.author} width="24" height="24" className="review-author-photo" onError={e => { e.target.style.display = 'none'; }} />}
                  <div className="review-meta">
                    <span className="review-author-name">{r.author}</span>
                    {r.date && <span className="review-author-date">{r.date}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="reviews-nav">
          <button onClick={prev} disabled={offset === 0}>&#8249;</button>
          <button onClick={next} disabled={offset >= maxOffset}>&#8250;</button>
        </div>
      </div>
    </div>
  );
}

// Build 3 sequential local images per product based on its image filename number
function getImages(product) {
  if (product.images?.length) return product.images;
  return [product.image].filter(Boolean);
}

const ProductCard = memo(function ProductCard({ product }) {
  const currency = useCurrency();
  const navigate = useNavigate();
  const productUrl = `/products/${product.category}/${product.model}`;
  const images = getImages(product);
  const displayImages = images.slice(0, 3); // Limit to 3 images
  const [activeImg, setActiveImg] = useState(0);
  const [hovered, setHovered] = useState(false);
  
  const price = Number(product.price || 0);
  const oldPrice = Number(product.oldPrice || 0);
  const hasDiscount = oldPrice > 0 && oldPrice > price;
  const isSparepart = product.category === 'spare-parts';
  const subCategoryLabel = product.subCategory ? product.subCategory.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';

  return (
    <div
      className={`home-product-card${hovered ? ' is-hovered' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => navigate(productUrl)}
      style={{ cursor: 'pointer' }}
    >
      <div className="home-product-img">
        <img
          src={getImageUrl(images[activeImg], 300)}
          alt={product.name}
          loading="lazy"
          width="300"
          height="300"
          onError={e => { e.target.src = '/images/scooter-1.png'; }}
        />
        {isSparepart && subCategoryLabel && (
          <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0, 102, 204, 0.95)', color: '#fff', padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, zIndex: 2 }}>
            For {subCategoryLabel}
          </div>
        )}
        <div className="img-thumbnails">
          {displayImages.map((img, i) => (
            <button
              key={i}
              className={`thumb${activeImg === i ? ' active' : ''}`}
              onClick={e => { e.stopPropagation(); setActiveImg(i); }}
            >
              <img src={getImageUrl(img, 60)} alt={`${product.name} image ${i + 1}`} loading="lazy" width="60" height="60" onError={e => { e.target.src = '/images/scooter-1.png'; }} />
            </button>
          ))}
        </div>
      </div>

      <div className="home-product-info">
        <div className="prd-rating">
          {'★★★★★'.split('').map((s, i) => <span key={i} className="star">{s}</span>)}
        </div>
        <span className="home-range">{product.range || ''}</span>
        <h3>{product.name.replace(/ Electric Scooter| Electric Bike| Scooter| Bike/gi, '').trim()}</h3>
        <div className="home-product-footer">
          <div className="home-price-wrap">
            {hasDiscount && <div className="price-old">{currency}{oldPrice.toFixed(2)}</div>}
            <div className="home-price">{currency}{price.toFixed(2)}</div>
          </div>
          <Link 
            to={`/products/${product.category}/${product.model}`} 
            className={`btn buy-btn ${product.inStock === false ? 'btn-soldout' : ''}`}
            onClick={e => e.stopPropagation()}
          >
            {product.inStock === false ? 'SOLD OUT' : 'BUY NOW'}
          </Link>
        </div>
      </div>
    </div>
  );
});

function Home() {
  const seo = useSeoSettings('home');
  const [slides, setSlides] = useState(null); // null = loading, [] = no slides, [...] = loaded
  const [products, setProducts] = useState([]);
  const [homeData, setHomeData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsTotal, setReviewsTotal] = useState(0);
  const [reviewsLoaded, setReviewsLoaded] = useState(false);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [homeDataLoaded, setHomeDataLoaded] = useState(false);
  const [homeCat, setHomeCat] = useState('electric-scooters');
  const [homeSearch, setHomeSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  // LCP fix: compute first slide URL at render time so preload tag is in initial HTML
  const firstSlideUrl = slides?.[0] ? getImageUrl(slides[0].image, 828) : null;

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setHomeSearch(searchInput), 200);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Auto-advance hero slides — use requestAnimationFrame to avoid forced reflow
  const [slideIdx, setSlideIdx] = useState(0);
  const slideCount = slides?.length || 0;
  const prevSlide = useCallback(() => setSlideIdx(i => (i - 1 + Math.max(slideCount, 1)) % Math.max(slideCount, 1)), [slideCount]);
  const nextSlide = useCallback(() => setSlideIdx(i => (i + 1) % Math.max(slideCount, 1)), [slideCount]);
  useEffect(() => {
    if (slideCount <= 1) return;
    let raf;
    let last = performance.now();
    const INTERVAL = 4000;
    const tick = (now) => {
      if (now - last >= INTERVAL) {
        last = now;
        setSlideIdx(i => (i + 1) % slideCount);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [slideCount]);
  useEffect(() => { setSlideIdx(0); }, [slideCount]);

  useEffect(() => {
    fetchSlides()
      .then(data => { setSlides(data?.length > 0 ? data : []); })
      .catch(() => { setSlides([]); });
    fetchProducts()
      .then(data => setProducts(data.filter(p => p.category !== 'spare-parts')))
      .catch(() => {})
      .finally(() => setProductsLoaded(true));
    fetchContent('home').then(setHomeData).catch(() => {}).finally(() => setHomeDataLoaded(true));
    fetchReviews()
      .then(data => {
        // Sort by date descending, take top 10
        const sorted = [...data].sort((a, b) => (b.iso_date || '').localeCompare(a.iso_date || ''));
        setReviewsTotal(data.length);
        setReviews(sorted.slice(0, 10));
      })
      .catch(() => {})
      .finally(() => setReviewsLoaded(true));
  }, []);

  const filteredProducts = useMemo(() =>
    (products || [])
      .filter(p => p.category === homeCat)
      .filter(p => !homeSearch.trim() || p.name?.toLowerCase().includes(homeSearch.toLowerCase()))
      .sort((a, b) => (a.price || 0) - (b.price || 0))
      .slice(0, 8),
    [products, homeCat, homeSearch]
  );

  return (
    <div className="home">

      <SEO
        title={seo.title || ""}
        description={seo.description || ""}
        url="/"
        keywords="e scooter, electric scooter, electric scooter sale, buy electric scooter online, electric scooter store"
        schema={getOrganizationSchema()}
      >
        {firstSlideUrl && <link rel="preload" as="image" href={firstSlideUrl} fetchpriority="high" />}
      </SEO>

      <div className="hero-slider-wrap">
        <div className="hero-native">
          {/* Placeholder: loading (null) or no slides — same aspect ratio, no layout shift */}
          {(slides === null || slides.length === 0) && (
            <div className="hero-slide hero-slide-active hero-placeholder">
              <div className="hero-placeholder-text">
                <div className="hero-placeholder-title">Green Wheels Scooters</div>
                <div className="hero-placeholder-sub">Electric Scooters &amp; E-Bikes Ireland</div>
              </div>
            </div>
          )}
          {/* Slides rendered in-place — no wrapper swap, no CLS */}
          {slides !== null && slides.length > 0 && (
            <>
              {slides.map((slide, i) => (
                <div key={i} className={`hero-slide${i === slideIdx ? ' hero-slide-active' : ''}`} aria-hidden={i !== slideIdx}>
                  <img
                    src={getImageUrl(slide.image, i === 0 ? 828 : 1400)}
                    alt={slide.title || 'Green Wheels Scooters'}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    fetchpriority={i === 0 ? 'high' : 'low'}
                    decoding={i === 0 ? 'sync' : 'async'}
                    width="1400" height="600"
                  />
                  {(slide.title || slide.cta) && (
                    <div className="hero-overlay">
                      {slide.title && <span className="slide-title">{slide.title}</span>}
                      {slide.subtitle && <p>{slide.subtitle}</p>}
                      {slide.cta && slide.ctaLink && <Link to={slide.ctaLink} className="btn hero-cta">{slide.cta}</Link>}
                    </div>
                  )}
                </div>
              ))}
              {slides.length > 1 && (
                <>
                  <button className="hero-arrow hero-prev" onClick={prevSlide} aria-label="Previous slide">&#8249;</button>
                  <button className="hero-arrow hero-next" onClick={nextSlide} aria-label="Next slide">&#8250;</button>
                  <div className="hero-dots">
                    {slides.map((_, i) => (
                      <button key={i} className={`hero-dot${i === slideIdx ? ' active' : ''}`} onClick={() => setSlideIdx(i)} aria-label={`Slide ${i + 1}`} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <ReviewsStrip reviews={reviews} totalCount={reviewsTotal} loaded={reviewsLoaded} />

      <section className="section">
        <div className="container">
          <h1 className="section-title">Featured Products</h1>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0, marginBottom: 24 }}>
            <button onClick={() => setHomeCat('electric-scooters')} style={{ padding: '12px 24px', border: 'none', background: homeCat === 'electric-scooters' ? '#1a1a2e' : '#f5f5f5', color: homeCat === 'electric-scooters' ? '#fff' : '#555', fontWeight: 700, fontSize: 14, cursor: 'pointer', borderRadius: '30px 0 0 30px', transition: 'all 0.2s' }}>
               Scooters
            </button>
            <button onClick={() => setHomeCat('electric-bikes')} style={{ padding: '12px 24px', border: 'none', background: homeCat === 'electric-bikes' ? '#1a1a2e' : '#f5f5f5', color: homeCat === 'electric-bikes' ? '#fff' : '#555', fontWeight: 700, fontSize: 14, cursor: 'pointer', borderRadius: '0 30px 30px 0', transition: 'all 0.2s' }}>
               E-Bikes
            </button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            <input
              type="text"
              placeholder="Search scooters & bikes..."
              value={homeSearch}
              onChange={e => setSearchInput(e.target.value)}
              style={{ width: '100%', maxWidth: 360, padding: '11px 18px', border: '1.5px solid #ddd', borderRadius: 30, fontSize: 14, outline: 'none', transition: 'border 0.2s' }}
              onFocus={e => e.target.style.borderColor = '#00d084'}
              onBlur={e => e.target.style.borderColor = '#ddd'}
            />
          </div>
          <div className="home-products-grid">
            {!productsLoaded
              ? Array.from({ length: 8 }).map((_, i) => <div key={i} className="home-product-card home-product-card-skeleton" aria-hidden="true" />)
              : filteredProducts.map(product => (
                  <ProductCard key={product._id || product.id} product={product} />
                ))}
          </div>
          <div className="view-all-wrap">
            <Link to="/products" className="btn">View All Products</Link>
          </div>
        </div>
      </section>

      {/* Reserve space while homeData loads so the section doesn't pop in and shove the footer down (CLS) */}
      {!homeDataLoaded && <section className="section why-us-section why-us-section-loading" aria-hidden="true" />}

      {homeData?.whyUsTitle && (
        <section className="section why-us-section">
          <div className="container why-us-container">
            <h2 className="why-us-heading">
              {homeData.whyUsTitle.split(/(GREEN)/i).map((part, i) =>
                part.toUpperCase() === 'GREEN'
                  ? <span key={i} className="why-us-green">{part}</span>
                  : part
              )}
            </h2>
            {((homeData.whyUsParas?.length ? homeData.whyUsParas : (homeData.whyUsText || '').split(/\\n\\n|\n\n/).filter(Boolean))).map((para, i) => (
              <p key={i} className="why-us-text">
                {para.split(/(Green)/i).map((chunk, j) =>
                  chunk.toLowerCase() === 'green'
                    ? <span key={j} className="why-us-green">{chunk}</span>
                    : chunk
                )}
              </p>
            ))}
            {homeData.whyUsCta && (
              <Link to={homeData.whyUsCtaLink || '/products'} className="btn why-us-btn">
                {homeData.whyUsCta}
              </Link>
            )}
            {homeData.whyUsContact && (
              <div className="why-us-contact">
                <span className="phone-icon"></span>
                <span>{homeData.whyUsContact}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {!homeDataLoaded && <section className="section service-section service-section-loading" aria-hidden="true" />}

      {homeData?.serviceTitle && (
        <section className="section service-section">
          <div className="container">
            <div className="service-content">
              <h2 className="service-heading">
                {homeData.serviceTitle.split(/(Green)/i).map((part, i) =>
                  part.toLowerCase() === 'green'
                    ? <span key={i} className="why-us-green">{part}</span>
                    : part
                )}
              </h2>
              {homeData.serviceSubtitle && <h3 className="service-subtitle">{homeData.serviceSubtitle}</h3>}
              {((homeData.serviceParas?.length ? homeData.serviceParas : (homeData.serviceText || '').split(/\\n\\n|\n\n/).filter(Boolean))).map((para, i) => (
                <p key={i} className="service-para">
                  {para.split(/(Green)/i).map((chunk, j) =>
                    chunk.toLowerCase() === 'green'
                      ? <span key={j} className="why-us-green">{chunk}</span>
                      : chunk
                  )}
                </p>
              ))}
              {homeData.serviceBullets?.length > 0 && (
                <ul className="service-bullets">
                  {homeData.serviceBullets.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              )}
              {homeData.serviceContact && (
                <div className="why-us-contact" style={{ marginTop: 24 }}>
                  <span className="phone-icon"></span>
                  <span>{homeData.serviceContact}</span>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

    </div>
  );
}

export default Home;

